/**
 * Everything that happens *because* the state changed.
 *
 * The reducer is pure (ADR 11), so all of it lives here: requesting branches when a chord
 * lands, saving, learning, and speculatively fetching what the player is most likely to
 * play next. Effects are attached to a store from outside and can be detached; nothing in
 * this file is a module-level singleton, so a test can run a whole session with a fake API
 * and a fake clock.
 *
 * ## The three behaviours worth explaining
 *
 * **The graph never blanks.** The reducer keeps the previous node's branch set when a new
 * chord lands, and `BRANCHES_LOADING` keeps whatever suggestions had already streamed in.
 * So between playing a chord and the first suggestion for it arriving, there is always
 * something to render — the previous column, faded. No effect here may delete a branch set.
 *
 * **Speculative prefetch.** The moment a branch set is complete, two of its branches are
 * fetched a step ahead: the *functional anchor* (the dominant or subdominant — the move
 * most players make most often, whatever else is on offer) and the branch matching the
 * movement kind this player has historically taken most. If the player then plays one of
 * them, its branch set is already in hand and the graph extends with no network wait at
 * all. Exactly two, because a third is mostly wasted tokens.
 *
 * **A correction refetches.** Correcting a misheard chord discards the branch sets grown
 * from the mistake (in the reducer), and the missing set for the current node is what
 * triggers a fresh request here.
 *
 * ## Two engines, one shape
 *
 * With the AI companion off — the default — no request is made at all: the built-in
 * engine fills the set from `state/heuristicBranches.ts` and the same three actions go
 * out in the same order, `BRANCHES_LOADING` → `BRANCH_RECEIVED` × n → `BRANCHES_READY`.
 * The reducer, the slot column and all fifteen UX states are untouched by the choice;
 * the set simply arrives in the same tick rather than in a second or two. Two things
 * *are* skipped, because both are about latency the local path does not have: the
 * speculative look-ahead, and `BRANCHES_FAILED`, which nothing on this path can reach —
 * there is no companion to be offline from.
 */

import {
  BRANCH_PATH_LIMIT,
  DEFAULT_BRANCH_COUNT,
  DEFAULT_RECOGNIZER_PARAMS,
  REGENERATE_HINT_LIMIT,
  currentNode,
  estimateKey,
  pcSetKey,
  toCanonical,
  type BranchRequest,
  type BranchSet,
  type BranchSuggestion,
  type CanonicalChord,
  type ModelChoice,
  type MovementKind,
  type PitchClass,
  type PreferenceHints,
  type RecognizerParams,
  type SessionNode,
  type SessionState,
} from '@hc/shared';
import { requestBranches, type LlmDebugPayload } from '../api/index.js';
import {
  generateBranchesFor,
  newSuggestionId,
  offeredPitchClassSets,
  rememberHeuristicBranches,
} from './heuristicBranches.js';
import { mostTakenKind, withTakenSuggestion, type PreferencesController } from './preferences.js';
import type { SessionPersistence } from './persistence.js';
import type { SessionStore } from './store.js';

// ---------------------------------------------------------------------------
// The port to the API layer
// ---------------------------------------------------------------------------

/** Where a suggestion came from. Mirrors `api/types.ts` so this file owns no wire shape. */
export type BranchSuggestionOrigin = 'model' | 'repair' | 'fallback';

export interface BranchRequestOptions {
  signal?: AbortSignal;
  onSuggestion?: (suggestion: BranchSuggestion, origin: BranchSuggestionOrigin) => void;
}

export interface BranchesResultLike {
  suggestions: BranchSuggestion[];
  status: 'ready' | 'partial' | 'fallback' | 'failed';
  cached: boolean;
  error?: string;
  /** Present only when the request carried `debug: true`. */
  debug?: LlmDebugPayload;
}

/**
 * The only thing the effects need from the network.
 *
 * Structural rather than an import of the concrete function, so tests supply a two-line
 * fake — but `defaultBranchesApi` below is a real assignment, so if the API module's shape
 * ever drifts from what this expects, it is a compile error here rather than a runtime
 * surprise in front of a piano.
 */
export interface BranchesApi {
  requestBranches(
    request: BranchRequest,
    options?: BranchRequestOptions,
  ): Promise<BranchesResultLike>;
}

export const defaultBranchesApi: BranchesApi = { requestBranches };

// ---------------------------------------------------------------------------
// Building the request
// ---------------------------------------------------------------------------

export interface BuildBranchRequestOptions {
  branchCount?: number;
  takenKinds?: readonly MovementKind[];
  ignoredKinds?: readonly MovementKind[];
  /**
   * The chord the branches grow from, when it is not the current node's — this is how a
   * prefetch asks "and if they then played *this*?".
   */
  hypotheticalChord?: CanonicalChord;
  /**
   * A one-line steer for this request alone ("somewhere distant"), from a spoken
   * "something else, but…". Trimmed and capped here rather than at the call site, so
   * every route into a branch request gets the same treatment.
   */
  regenerateHint?: string;
  /**
   * The player's model tier. Part of the server's cache key, so a speculative request and
   * the real one it is speculating about must carry the same value or the look-ahead
   * cannot hit — which is exactly what `prefetchParity.test.ts` pins.
   */
  modelChoice?: ModelChoice;
  /** Ask the answer to carry its prompt. Only while "Show model prompts" is on. */
  debug?: boolean;
}

function pathEntry(node: SessionNode): BranchRequest['path'][number] {
  return {
    symbol: node.chord.symbol,
    pitchClasses: node.chord.pitchClasses,
    bassPc: node.chord.bassPc,
  };
}

/**
 * Assemble a `BranchRequest` from the session.
 *
 * `path` is the history *before* the current chord: the server's cache key is
 * `path ++ current`, so including the current chord in both would key every request on a
 * doubled chord and never hit.
 */
export function buildBranchRequest(
  state: SessionState,
  hints: PreferenceHints,
  options: BuildBranchRequestOptions = {},
): BranchRequest | null {
  const node = currentNode(state);
  const current = options.hypotheticalChord ?? node?.chord;
  if (!current) return null;

  const history = options.hypotheticalChord ? state.path : state.path.slice(0, -1);
  const keyEstimate = options.hypotheticalChord
    ? estimateKey([
        ...state.path,
        {
          id: 'hypothetical',
          chord: current,
          source: 'manual',
          playedAt: 0,
          keyAtTime: state.keyEstimate,
        },
      ])
    : state.keyEstimate;

  const request: BranchRequest = {
    sessionId: state.sessionId,
    perspectives: [...state.perspectives],
    adventurousness: state.adventurousness,
    path: history.slice(-BRANCH_PATH_LIMIT).map(pathEntry),
    current: {
      symbol: current.symbol,
      pitchClasses: current.pitchClasses,
      rootPc: current.rootPc,
      bassPc: current.bassPc,
    },
    keyEstimate,
    takenKinds: [...(options.takenKinds ?? [])],
    ignoredKinds: [...(options.ignoredKinds ?? [])],
    preferenceHints: hints,
    branchCount: options.branchCount ?? DEFAULT_BRANCH_COUNT,
    detail: 'concise',
  };
  if (state.intention !== undefined) request.intention = state.intention;
  // Capped rather than rejected: a hint four characters too long is not a reason to send
  // no hint at all, and the server would refuse the whole request over it.
  const hint = options.regenerateHint?.trim();
  if (hint) request.regenerateHint = hint.slice(0, REGENERATE_HINT_LIMIT);
  if (options.modelChoice) request.modelChoice = options.modelChoice;
  // Omitted rather than sent as `false`, so a request made with the setting off is
  // byte-identical to one made before the field existed — and cannot key differently.
  if (options.debug) request.debug = true;
  return request;
}

// ---------------------------------------------------------------------------
// Speculative prefetch
// ---------------------------------------------------------------------------

/**
 * The moves that are worth a speculative request regardless of taste. A dominant or
 * subdominant resolution is the single most likely continuation from almost any chord in
 * almost any style, which is exactly what makes it worth having in hand early.
 */
export const FUNCTIONAL_ANCHOR_KINDS: readonly MovementKind[] = [
  'functional-dominant',
  'functional-subdominant',
];

/** Two. A third speculative request buys a few percent of hit rate for 50% more tokens. */
export const PREFETCH_LIMIT = 2;

/** Prefetched sets are kept small and short-lived; they are a latency trick, not a cache. */
export const PREFETCH_CACHE_LIMIT = 8;
export const PREFETCH_TTL_MS = 5 * 60_000;

/**
 * The two branches to fetch a step ahead: the functional anchor, and the kind this player
 * reaches for most. When there is no anchor and no history, this falls back to the least
 * adventurous branch and then simply the next one — which is still a better guess than none.
 */
export function pickPrefetchCandidates(set: BranchSet, hints: PreferenceHints): BranchSuggestion[] {
  if (set.suggestions.length === 0) return [];
  const chosen: BranchSuggestion[] = [];

  const anchor =
    set.suggestions.find((s) => FUNCTIONAL_ANCHOR_KINDS.includes(s.movementKind)) ??
    [...set.suggestions].sort((a, b) => a.adventurousness - b.adventurousness)[0];
  if (anchor) chosen.push(anchor);

  const favourite = mostTakenKind(hints);
  const habitual = favourite
    ? set.suggestions.find((s) => s.movementKind === favourite && !chosen.includes(s))
    : undefined;
  const runnerUp = habitual ?? set.suggestions.find((s) => !chosen.includes(s));
  if (runnerUp) chosen.push(runnerUp);

  return chosen.slice(0, PREFETCH_LIMIT);
}

// ---------------------------------------------------------------------------
// The effects
// ---------------------------------------------------------------------------

export interface SessionEffectsOptions {
  store: SessionStore;
  persistence: SessionPersistence;
  preferences: PreferencesController;
  api?: BranchesApi;
  /** How many branches to ask for. 4..6; see `MIN_BRANCH_COUNT`/`MAX_BRANCH_COUNT`. */
  branchCount?: number;
  /** Set false to save the tokens — the settings screen exposes this. */
  prefetch?: boolean;
  /** The recogniser parameters in force, for the correction log. */
  getRecognizerParams?: () => RecognizerParams;
  /**
   * The player's model tier, read at request time.
   *
   * A getter rather than a value for the same reason `getRecognizerParams` is one: the
   * effects attach at mount and the setting changes underneath them, and an effects layer
   * that captured the tier at attach time would go on asking for the old model until
   * something unrelated caused a re-attach.
   */
  getModelChoice?: () => ModelChoice | undefined;
  /**
   * Whether branches come from the model or from the built-in engine, read at request
   * time — a getter for the same reason the tier is one: it is a setting somebody can
   * change between two chords, and a value captured at attach time would go on asking the
   * network long after the switch was thrown.
   *
   * Defaults to **false**, which is the app's default and also the safer default for a
   * caller that forgot: a test or a host that omits it gets no network at all rather than
   * a request nobody expected.
   */
  getAiCompanion?: () => boolean;
  /** Whether to ask every request to carry its prompt. Read at request time, as above. */
  getDebug?: () => boolean;
  /**
   * The built-in engine's entropy and its ids. Injected only so a test can pin a set;
   * the engine itself requires both, and defaulting them *here* rather than there is what
   * keeps the impurity in the effects layer where the rest of it already lives.
   */
  rng?: () => number;
  mintSuggestionId?: () => string;
  /**
   * A prompt came back. Called only for requests that asked for one, which is only while
   * the setting is on.
   */
  onDebug?: (route: 'branches' | 'branches:look-ahead', debug: LlmDebugPayload) => void;
  /**
   * Locally derived continuations, used when the request fails outright. The server
   * supplies its own fallbacks in the normal case; this is for a dead network, where the
   * request never arrives at all.
   */
  localFallback?: (state: SessionState, nodeId: string) => BranchSuggestion[];
  now?: () => number;
  onError?: (error: unknown, context: string) => void;
  /** Set false in tests that do not want a `document` listener. */
  saveOnPageHide?: boolean;
}

/** One-shot options for a forced branch request. Nothing here outlives the request. */
export interface RequestBranchesOptions {
  /**
   * A steer for this fetch only — "somewhere distant". It is passed down as an argument
   * rather than held anywhere, which is what makes it impossible for it to colour the
   * next chord's branches or a speculative prefetch.
   */
  hint?: string;
}

export interface SessionEffects {
  /** Force a fetch for a node, whatever state its branch set is in. The retry glyph. */
  requestBranchesFor(nodeId: string, options?: RequestBranchesOptions): void;
  /** Await every pending write. */
  flush(): Promise<void>;
  detach(): void;
  /** Diagnostics for the tuning screen. */
  stats(): { prefetched: number; inFlightNodeId: string | null; requests: number };
}

/** The key a prefetched set is filed under: the sonority it was fetched *from*. */
function prefetchKey(chord: CanonicalChord): string {
  return pcSetKey(chord.pitchClasses);
}

export function attachSessionEffects(options: SessionEffectsOptions): SessionEffects {
  const { store, persistence, preferences } = options;
  const api = options.api ?? defaultBranchesApi;
  const branchCount = options.branchCount ?? DEFAULT_BRANCH_COUNT;
  const prefetchEnabled = options.prefetch ?? true;
  const getRecognizerParams = options.getRecognizerParams ?? (() => DEFAULT_RECOGNIZER_PARAMS);
  const getModelChoice = options.getModelChoice ?? (() => undefined);
  const getAiCompanion = options.getAiCompanion ?? (() => false);
  const getDebug = options.getDebug ?? (() => false);
  const rng = options.rng ?? Math.random;
  const mintSuggestionId = options.mintSuggestionId ?? newSuggestionId;
  const now = options.now ?? (() => Date.now());
  const onError = options.onError ?? (() => {});

  /**
   * The two fields every branch request carries, whoever is asking.
   *
   * Read through one function so a speculative request and a real one can never disagree
   * about them — the server's cache key includes the model, so disagreeing would mean the
   * look-ahead silently never hit.
   */
  function llmOptions(): { modelChoice?: ModelChoice; debug?: boolean } {
    const modelChoice = getModelChoice();
    return { ...(modelChoice ? { modelChoice } : {}), ...(getDebug() ? { debug: true } : {}) };
  }

  function reportDebug(
    route: 'branches' | 'branches:look-ahead',
    result: BranchesResultLike,
  ): void {
    if (result.debug) options.onDebug?.(route, result.debug);
  }

  /** Session-scoped movement tallies. These ride on the request; they are not persisted. */
  const takenKinds: MovementKind[] = [];
  const ignoredKinds: MovementKind[] = [];

  const prefetched = new Map<string, { suggestions: BranchSuggestion[]; at: number }>();
  const prefetchInFlight = new Set<string>();
  let inFlight: { nodeId: string; controller: AbortController } | null = null;
  let requests = 0;
  let lastRecordedKey = '';
  let detached = false;

  void preferences.ready();

  // -- branch requests ------------------------------------------------------

  function takePrefetched(chord: CanonicalChord): BranchSuggestion[] | null {
    const key = prefetchKey(chord);
    const hit = prefetched.get(key);
    if (!hit) return null;
    prefetched.delete(key);
    if (now() - hit.at > PREFETCH_TTL_MS) return null;
    return hit.suggestions;
  }

  function ensureBranches(nodeId: string | null, force = false, regenerateHint?: string): void {
    if (detached || !nodeId) return;
    const state = store.getState();
    const node = state.path.find((entry) => entry.id === nodeId);
    if (!node) return;

    if (!force) {
      const existing = state.branchSets[nodeId];
      // `failed` is the one status worth retrying automatically; the others are either
      // in flight or already answered.
      if (existing && existing.status !== 'failed') return;
      if (inFlight?.nodeId === nodeId) return;
    }

    if (inFlight) inFlight.controller.abort();
    store.dispatch({ type: 'BRANCHES_LOADING', nodeId });

    // Before the prefetch cache, not after: a set fetched while the companion was on must
    // not be served after it was switched off. `BRANCHES_LOADING` still went out, so the
    // column has opened its slots and the engine fills them in the same tick.
    if (!getAiCompanion()) {
      fillBranchesLocally(state, nodeId, force);
      return;
    }

    // A steer is a request for something *other* than what is in hand, so the
    // speculative set — fetched with no steer at all — cannot be the answer to it.
    const cached = regenerateHint ? null : takePrefetched(node.chord);
    if (cached && cached.length > 0) {
      for (const suggestion of cached) {
        store.dispatch({ type: 'BRANCH_RECEIVED', nodeId, suggestion });
      }
      store.dispatch({ type: 'BRANCHES_READY', nodeId });
      schedulePrefetch(nodeId);
      return;
    }

    const request = buildBranchRequest(state, preferences.get(), {
      branchCount,
      takenKinds,
      ignoredKinds,
      ...llmOptions(),
      ...(regenerateHint ? { regenerateHint } : {}),
    });
    if (!request) return;

    const controller = new AbortController();
    inFlight = { nodeId, controller };
    requests += 1;

    void api
      .requestBranches(request, {
        signal: controller.signal,
        onSuggestion: (suggestion) => {
          if (controller.signal.aborted) return;
          store.dispatch({ type: 'BRANCH_RECEIVED', nodeId, suggestion });
        },
      })
      .then((result) => {
        if (controller.signal.aborted) return;
        reportDebug('branches', result);
        if (result.status === 'failed') {
          store.dispatch({
            type: 'BRANCHES_FAILED',
            nodeId,
            ...withFallback(result.suggestions, nodeId),
          });
          return;
        }
        if (result.status === 'fallback') {
          // The model did not answer; these came from the server's static table. The set
          // is usable and the session is honestly marked unhealthy.
          store.dispatch({ type: 'BRANCHES_FAILED', nodeId, fallback: result.suggestions });
          return;
        }
        store.dispatch({ type: 'BRANCHES_READY', nodeId });
        schedulePrefetch(nodeId);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        onError(error, 'branches');
        store.dispatch({ type: 'BRANCHES_FAILED', nodeId, ...withFallback([], nodeId) });
      })
      .finally(() => {
        if (inFlight?.controller === controller) inFlight = null;
      });
  }

  /**
   * The built-in engine's answer, through the same actions the streamed path uses.
   *
   * Synchronous, and that is the point: `BRANCH_RECEIVED` per suggestion and then
   * `BRANCHES_READY`, in output order, exactly as the stream produces them — so the slot
   * column, the reducer and every derived UX state see the shape they have always seen,
   * with the waiting removed. Nothing here can dispatch `BRANCHES_FAILED`: the engine is
   * total, and a set that cannot fail is a session that cannot be "companion offline".
   */
  function fillBranchesLocally(state: SessionState, nodeId: string, force: boolean): void {
    requests += 1;

    /*
     * "Show me something else" is the only reason to exclude anything.
     *
     * A forced fill over a set that already has suggestions is a regeneration — the retry
     * glyph, or the command bar's "something else" — and handing the engine the sets
     * already on screen is what makes the second answer a different answer rather than
     * the same weighted draw run twice. An unforced fill has nothing to exclude, and a
     * correction has nothing either: the reducer discards the branch set of the chord it
     * corrected, so the old options are already gone.
     */
    const existing = force ? state.branchSets[nodeId] : undefined;
    const excluded = existing ? offeredPitchClassSets(existing.suggestions) : [];

    const branches = generateBranchesFor(state, {
      branchCount,
      preferenceHints: preferences.get(),
      takenKinds,
      ignoredKinds,
      ...(excluded.length > 0 ? { excludePitchClassSets: excluded } : {}),
      rng,
      mintId: mintSuggestionId,
    });
    // Filed before anything is dispatched: a card can be expanded the instant it renders,
    // and its paragraph has to already be there when it asks.
    rememberHeuristicBranches(branches);

    for (const branch of branches) {
      store.dispatch({ type: 'BRANCH_RECEIVED', nodeId, suggestion: branch.suggestion });
    }
    store.dispatch({ type: 'BRANCHES_READY', nodeId });
  }

  function withFallback(
    partial: readonly BranchSuggestion[],
    nodeId: string,
  ): { fallback?: BranchSuggestion[] } {
    const local = options.localFallback?.(store.getState(), nodeId) ?? [];
    const fallback = partial.length > 0 ? [...partial] : local;
    return fallback.length > 0 ? { fallback } : {};
  }

  // -- prefetch -------------------------------------------------------------

  function schedulePrefetch(nodeId: string): void {
    if (!prefetchEnabled || detached) return;
    // There is no wait to hide. The look-ahead exists to have the next set in hand before
    // the player asks for it; the engine has it in hand the moment they do.
    if (!getAiCompanion()) return;
    const state = store.getState();
    const set = state.branchSets[nodeId];
    if (!set || set.suggestions.length === 0) return;

    for (const candidate of pickPrefetchCandidates(set, preferences.get())) {
      const chord = toCanonical(candidate.chord);
      const key = prefetchKey(chord);
      if (prefetched.has(key) || prefetchInFlight.has(key)) continue;

      /*
       * The learning fields as they will be *after* the move, not as they are now.
       *
       * `recordChoice` runs before `ensureBranches` when a chord lands, so by the time the
       * real request for this chord is built, the kind just taken has been counted and the
       * kinds passed over have been noted. A speculative request that sent the pre-move
       * tallies would therefore differ from the request it is speculating about — see
       * `prefetchParity.test.ts`, which deep-compares the two. Mirrors `recordChoice`
       * exactly, and reuses the controller's own `withTakenSuggestion` rather than
       * restating what taking a branch does to the hints.
       */
      const hypotheticalTaken = [...takenKinds, candidate.movementKind];
      const hypotheticalIgnored = [
        ...ignoredKinds,
        ...set.suggestions.filter((s) => s.id !== candidate.id).map((s) => s.movementKind),
      ];

      const request = buildBranchRequest(state, withTakenSuggestion(preferences.get(), candidate), {
        branchCount,
        takenKinds: hypotheticalTaken,
        ignoredKinds: hypotheticalIgnored,
        hypotheticalChord: chord,
        ...llmOptions(),
      });
      if (!request) continue;

      prefetchInFlight.add(key);
      void api
        .requestBranches(request)
        .then((result) => {
          // Recorded even when the set came back empty: "the look-ahead asked and got
          // nothing" is exactly as informative as "the look-ahead asked and got five".
          reportDebug('branches:look-ahead', result);
          if (detached || result.suggestions.length === 0) return;
          prefetched.set(key, { suggestions: result.suggestions, at: now() });
          // Oldest out first. `Map` iterates in insertion order, which is what we want.
          while (prefetched.size > PREFETCH_CACHE_LIMIT) {
            const oldest = prefetched.keys().next().value;
            if (oldest === undefined) break;
            prefetched.delete(oldest);
          }
        })
        .catch((error: unknown) => {
          // A speculative request that fails costs nothing and is not worth reporting
          // beyond the diagnostics hook.
          onError(error, 'prefetch');
        })
        .finally(() => {
          prefetchInFlight.delete(key);
        });
    }
  }

  // -- learning -------------------------------------------------------------

  function recordChoice(state: SessionState): void {
    const node = currentNode(state);
    if (!node) return;

    if (node.fromSuggestionId) {
      const priorNode = state.path[state.path.length - 2];
      const set = priorNode ? state.branchSets[priorNode.id] : undefined;
      const taken = set?.suggestions.find((s) => s.id === node.fromSuggestionId);
      if (taken) {
        preferences.recordTaken(taken);
        takenKinds.push(taken.movementKind);
        for (const other of set?.suggestions ?? []) {
          if (other.id !== taken.id) ignoredKinds.push(other.movementKind);
        }
      }
    }

    // Only count a key once it is settled, and once per stretch of being in it —
    // otherwise a long session in one key would swamp `commonKeys` on its own.
    const key = `${state.keyEstimate.tonicPc}:${state.keyEstimate.mode}`;
    if (state.keyEstimate.confidence >= 0.6 && key !== lastRecordedKey) {
      lastRecordedKey = key;
      preferences.recordKey(state.keyEstimate.tonicPc as PitchClass);
    }
  }

  function logCorrection(state: SessionState, previous: SessionState, nodeId: string): void {
    const before = previous.path.find((node) => node.id === nodeId);
    const after = state.path.find((node) => node.id === nodeId);
    if (!before || !after || !before.detected) return;
    /*
     * Only a played chord is a recognition.
     *
     * A tapped or typed one carries a *synthesised* one-hot chroma (`synthesizeDetection`)
     * and a chord nothing ever misheard, so renaming it is a typo fix, not evidence about
     * the recogniser. Writing those rows into the log makes the dataset partly fiction —
     * and re-correcting an already-corrected node would file the previous correction under
     * `emitted`, which nothing emitted.
     */
    if (before.source !== 'audio') return;
    void persistence
      .logCorrection({
        chromaIntegrated: before.detected.chroma,
        emitted: before.chord,
        corrected: after.chord,
        params: getRecognizerParams(),
        // Stamped explicitly rather than left to the default, so a record written here is
        // the same shape as one written by the ✓/✗ chrome and neither has to be inferred.
        verdict: 'corrected',
        confidence: before.detected.confidence,
        ts: now(),
        nodeId,
        sessionId: state.sessionId,
      })
      .catch((error: unknown) => onError(error, 'correction-log'));
  }

  // -- the subscription -----------------------------------------------------

  const unsubscribe = store.subscribe((state, action, previous) => {
    if (detached) return;

    // Every change is a candidate for autosave; the debounce decides which ones cost a write.
    persistence.scheduleSave(state);

    switch (action.type) {
      case 'CHORD_DETECTED':
        if (state.currentNodeId !== previous.currentNodeId) {
          recordChoice(state);
          ensureBranches(state.currentNodeId);
        } else if (
          // The arpeggio case: the node did not change but its chord did, so the branches
          // that were requested for it are gone and need requesting again.
          state.currentNodeId &&
          !state.branchSets[state.currentNodeId] &&
          previous.branchSets[state.currentNodeId]
        ) {
          ensureBranches(state.currentNodeId, true);
        }
        break;

      case 'CORRECTION_APPLIED':
        logCorrection(state, previous, action.nodeId);
        ensureBranches(state.currentNodeId, true);
        break;

      case 'BRANCHES_READY':
      case 'BRANCHES_FAILED':
        schedulePrefetch(action.nodeId);
        break;

      case 'SET_ADVENTUROUSNESS':
        preferences.recordAdventurousness(action.value);
        break;

      case 'SET_PERSPECTIVES':
      case 'SET_INTENTION':
        // The graph on screen was generated under different instructions.
        ensureBranches(state.currentNodeId, true);
        break;

      case 'RESTORED':
      case 'STEP_BACK':
        ensureBranches(state.currentNodeId);
        break;

      default:
        break;
    }
  });

  // A tab going away on iOS gets no second chance, so the debounce is bypassed.
  let onPageHide: (() => void) | null = null;
  if ((options.saveOnPageHide ?? true) && typeof document !== 'undefined') {
    onPageHide = () => {
      if (document.visibilityState === 'hidden') void persistence.save(store.getState());
    };
    document.addEventListener('visibilitychange', onPageHide);
  }

  // A session restored before the effects were attached still needs its branches.
  ensureBranches(store.getState().currentNodeId);

  return {
    requestBranchesFor(nodeId, options) {
      ensureBranches(nodeId, true, options?.hint);
    },
    async flush() {
      await persistence.flush();
      await preferences.flush();
    },
    detach() {
      detached = true;
      unsubscribe();
      inFlight?.controller.abort();
      inFlight = null;
      if (onPageHide && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onPageHide);
      }
      persistence.dispose();
    },
    stats: () => ({
      prefetched: prefetched.size,
      inFlightNodeId: inFlight?.nodeId ?? null,
      requests,
    }),
  };
}
